import React from "react";
import AccessRequestStatusChip from "../AccessRequestStatusChip";
import { ColumnDef } from "@tanstack/react-table";
import { DATE_FORMATS } from "constants/configs";
import { AccessRequestStatus, IAccessRequestEntity } from "models/AccessRequest.model";
import { useTranslation } from "react-i18next";
import { formatDateStr } from "utils/formatDateStr";
import AccessRequestActionButton from "components/AccessRequestTable/AccessRequestActionButton";

type Args = {
  onClickOpenApplication: (accessRequestGuid: string, applicationId: number) => void;
  onClickRequestAccess: (applicationId: number) => void;
};

export const useMyAccessRequestColumns = (args: Args) => {
  const { onClickOpenApplication, onClickRequestAccess } = args;
  const { t } = useTranslation();

  const getDescriptionText = React.useCallback(
    (row: IAccessRequestEntity) => {
      const { createdDate, expireTime, status } = row;

      const formattedCreatedDate = formatDateStr(createdDate, DATE_FORMATS.FULL_DATE);
      const formattedExpireTime = formatDateStr(expireTime, DATE_FORMATS.FULL_DATE);

      switch (status) {
        case AccessRequestStatus.Pending:
          return t("Requested {{createdDate}} · Expires {{expireTime}}", {
            createdDate: formattedCreatedDate,
            expireTime: formattedExpireTime,
          });
        case AccessRequestStatus.Denied:
          return t("Declined {{createdDate}}", { createdDate: formattedCreatedDate });
        case AccessRequestStatus.Expired:
          return t("Expired on {{expireTime}}", { expireTime: formattedExpireTime });
        case AccessRequestStatus.Approved:
        default:
          return "";
      }
    },
    [t]
  );

  const accessRequestColumns: ColumnDef<IAccessRequestEntity, any>[] = React.useMemo(
    () => [
      {
        header: t("Project ID"),
        enableSorting: true,
        size: 160,
        accessorFn: (originalRow) => originalRow.consentNumber || originalRow.applicationNumber,
      },
      {
        accessorKey: "address",
        header: t("Address"),
        enableSorting: true,
      },
      {
        id: "description",
        header: t("Description"),
        enableSorting: true,
        accessorFn: getDescriptionText,
        cell: (props) => getDescriptionText(props.row.original),
        sortingFn: (rowA, rowB) => {
          const dateA = rowA.original.completionDate || rowA.original.createdDate;
          const dateB = rowB.original.completionDate || rowB.original.createdDate;

          if (dateA < dateB) {
            return -1;
          }
          return dateA > dateB ? 1 : 0;
        },
      },
      {
        accessorKey: "status",
        header: t("Status"),
        enableSorting: true,
        size: 160,
        cell: (props) => <AccessRequestStatusChip {...props} />,
      },
      {
        header: t("Action"),
        enableSorting: false,
        size: 160,
        cell: (props) => {
          const { applicationId, status, applicationStatus, requestGuid } = props.row.original;

          if (status === AccessRequestStatus.Pending) {
            return null;
          }

          return (
            <AccessRequestActionButton
              applicationId={applicationId}
              applicationStatus={applicationStatus}
              hasApplicationAccess={status === AccessRequestStatus.Approved}
              onClickOpenApplication={(applicationId) => onClickOpenApplication(requestGuid, applicationId)}
              onClickRequestAccess={onClickRequestAccess}
            />
          );
        },
      },
    ],
    [t, onClickOpenApplication, onClickRequestAccess, getDescriptionText]
  );

  return accessRequestColumns;
};
